import React, { useMemo, useState } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import {
  IAnomaliesV2SeriesPoint,
  TAnomaliesAlertFamily,
  TAnomaliesPrimaryMetric,
} from '../../shared/types'
import { varClass } from '../../shared'
import {
  METRIC_LABELS,
  METRIC_UNIT,
  formatInteger,
  formatMetricValue,
  robustYMax,
  tickFormatterFor,
} from '../shared'

const xTickFormatter = (s: string) => {
  try {
    return format(parseISO(s), 'MMM d')
  } catch {
    return s
  }
}

const safe = (v: number | null | undefined): number | null =>
  v == null || isNaN(v) ? null : v

// Picks the right series for the alert family: drift shows current 30d +
// 6-month anchor (the comparison the detector uses); rejection shows current
// vs baseline rejection rate; acute shows the 7d rolling line.
const seriesPickers = (
  family: TAnomaliesAlertFamily,
  metric: TAnomaliesPrimaryMetric
): {
  primary: keyof IAnomaliesV2SeriesPoint | null
  primaryLabel: string
  reference: keyof IAnomaliesV2SeriesPoint | null
  referenceLabel: string
  rolling: keyof IAnomaliesV2SeriesPoint | null
  rollingLabel: string
} => {
  if (family === 'drift') {
    if (metric === 'cr') {
      return {
        primary: 'cr30dCurr',
        primaryLabel: 'CR 30d',
        reference: 'cr180dAnchor',
        referenceLabel: '6m baseline',
        rolling: 'cr7d',
        rollingLabel: 'CR 7d',
      }
    }
    if (metric === 'epc') {
      return {
        primary: 'epc30dCurr',
        primaryLabel: 'EPC 30d',
        reference: 'epc180dAnchor',
        referenceLabel: '6m baseline',
        rolling: 'epc7d',
        rollingLabel: 'EPC 7d',
      }
    }
    if (metric === 'spc') {
      return {
        primary: 'spc30dCurr',
        primaryLabel: 'SPC 30d',
        reference: 'spc180dAnchor',
        referenceLabel: '6m baseline',
        rolling: 'spc7d',
        rollingLabel: 'SPC 7d',
      }
    }
  }
  if (family === 'rejection') {
    // Rejection is now volume-shaped: count of rejections REPORTED (by report
    // date, not origin date) in the last 7d. The reference baseline is the
    // weekly-equivalent of the 30d baseline count; computed at render time
    // because it isn't a single field on the series row.
    return {
      primary: 'reportedRej7d',
      primaryLabel: 'Reported rejections 7d',
      reference: null,
      referenceLabel: 'Baseline (weekly equivalent)',
      rolling: null,
      rollingLabel: '',
    }
  }
  // Acute — the per-metric rolling 7d line is what's evaluated against the
  // 30d / 90d baselines, so that's what matters in the chart.
  if (metric === 'cr') {
    return {
      primary: 'cr7d',
      primaryLabel: 'CR 7d',
      reference: 'cr30dCurr',
      referenceLabel: 'CR 30d',
      rolling: null,
      rollingLabel: '',
    }
  }
  if (metric === 'epc') {
    return {
      primary: 'epc7d',
      primaryLabel: 'EPC 7d',
      reference: 'epc30dCurr',
      referenceLabel: 'EPC 30d',
      rolling: null,
      rollingLabel: '',
    }
  }
  if (metric === 'spc') {
    return {
      primary: 'spc7d',
      primaryLabel: 'SPC 7d',
      reference: 'spc30dCurr',
      referenceLabel: 'SPC 30d',
      rolling: null,
      rollingLabel: '',
    }
  }
  // clicks / conversions — daily count is the primary signal for an acute
  // drop, with 7d rolling overlaid for smoothing context.
  if (metric === 'clicks') {
    return {
      primary: 'clicks',
      primaryLabel: 'Daily clicks',
      reference: null,
      referenceLabel: '',
      rolling: 'clicks7d',
      rollingLabel: 'Clicks 7d',
    }
  }
  if (metric === 'conversions') {
    return {
      primary: 'conversions',
      primaryLabel: 'Daily conversions',
      reference: null,
      referenceLabel: '',
      rolling: 'conversions7d',
      rollingLabel: 'Conversions 7d',
    }
  }
  return {
    primary: null,
    primaryLabel: '',
    reference: null,
    referenceLabel: '',
    rolling: null,
    rollingLabel: '',
  }
}

export const PrimaryMetricChart = (props: {
  series: IAnomaliesV2SeriesPoint[]
  family: TAnomaliesAlertFamily
  metric: TAnomaliesPrimaryMetric
}) => {
  const { series, family, metric } = props

  const pickers = useMemo(() => seriesPickers(family, metric), [family, metric])
  // Rejection-family rendering is volume-shaped (integer count) regardless of
  // the metric's nominal "percent" unit, so override the formatter here.
  const isRejectionVolume = family === 'rejection'
  const unit = isRejectionVolume ? 'number' : METRIC_UNIT[metric]
  const [scale, setScale] = useState<'normal' | 'full'>('full')

  const chartData = useMemo(
    () =>
      series.map((point) => {
        // Derived weekly-equivalent baseline for the rejection volume chart:
        //   reportedRej30dBase / 30 * 7
        // Computed once per row so the dashed reference line is comparable to
        // the 7d primary line on the same axis.
        const rejWeeklyBaseline =
          isRejectionVolume && point.reportedRej30dBase != null
            ? (point.reportedRej30dBase / 30) * 7
            : null

        return {
          date: point.eventDate,
          primary: pickers.primary
            ? safe(point[pickers.primary] as number | null | undefined)
            : null,
          reference: isRejectionVolume
            ? rejWeeklyBaseline
            : pickers.reference
            ? safe(point[pickers.reference] as number | null | undefined)
            : null,
          rolling: pickers.rolling
            ? safe(point[pickers.rolling] as number | null | undefined)
            : null,
        }
      }),
    [series, pickers, isRejectionVolume]
  )

  // Y-axis cap when "Normal" — uses P95 across the visible series so a Q4
  // spike doesn't crush every other day.
  const yMax = useMemo(() => {
    if (scale === 'full') return undefined
    const all: (number | null | undefined)[] = []
    for (const r of chartData) {
      all.push(r.primary, r.reference, r.rolling)
    }
    const cap = robustYMax(all)
    return cap == null ? undefined : cap
  }, [chartData, scale])

  const isClipped = useMemo(() => {
    if (scale === 'full' || yMax == null) return false
    return chartData.some(
      (r) =>
        (r.primary != null && r.primary > yMax) ||
        (r.reference != null && r.reference > yMax) ||
        (r.rolling != null && r.rolling > yMax)
    )
  }, [chartData, yMax, scale])

  if (!pickers.primary) {
    return (
      <div className="card anomalies-chart">
        <div className="card-body p-t-1">
          <div className="text-dark text-bolder m-b-3">
            Primary metric trend
          </div>
          <div className="text-light text-small p-y-10 text-center">
            No daily series for {METRIC_LABELS[metric]}.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card anomalies-chart">
      <div className="card-body p-t-1">
        <div className="row row-space-between row-center m-b-3">
          <div className="text-dark text-bolder">
            {chartTitleFor(metric, family)}
          </div>
          <div className="row row-center" style={{ gap: 12, flexWrap: 'wrap' }}>
            <div className="anomalies-chart-legend">
              <Legend color="#4F9EEC" label={pickers.primaryLabel} />
              {(pickers.reference || isRejectionVolume) && (
                <Legend color="#828B9E" dashed label={pickers.referenceLabel} />
              )}
              {pickers.rolling && (
                <Legend color="#5DC97E" label={pickers.rollingLabel} />
              )}
            </div>
            <div className="row row-center" style={{ gap: 8 }}>
              <span className="text-light text-small">Scale:</span>
              <div className="anomalies-trend-toggle-group">
                <button
                  type="button"
                  className={varClass({
                    'anomalies-trend-toggle': true,
                    'active': scale === 'normal',
                  })}
                  onClick={() => setScale('normal')}
                  title="Cap y-axis at P95 so peaks don't crush the rest of the series."
                >
                  Normal
                </button>
                <button
                  type="button"
                  className={varClass({
                    'anomalies-trend-toggle': true,
                    'active': scale === 'full',
                  })}
                  onClick={() => setScale('full')}
                >
                  Full
                </button>
              </div>
            </div>
          </div>
        </div>
        <ResponsiveContainer width="99.9%" height={340}>
          <ComposedChart
            data={chartData}
            margin={{ top: 8, left: 0, right: 0, bottom: 0 }}
          >
            <CartesianGrid
              horizontal
              vertical={false}
              strokeDasharray="6"
              stroke="#EDEFF2"
            />
            <Tooltip
              content={
                <PrimaryTooltip
                  metric={metric}
                  primaryLabel={pickers.primaryLabel}
                  referenceLabel={pickers.referenceLabel}
                  rollingLabel={pickers.rollingLabel}
                  forceInteger={isRejectionVolume}
                />
              }
            />
            {(pickers.reference || isRejectionVolume) && (
              <Line
                dataKey="reference"
                stroke="#828B9E"
                strokeWidth={1.5}
                strokeDasharray="6 4"
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            )}
            {pickers.rolling && (
              <Line
                dataKey="rolling"
                stroke="#5DC97E"
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
            )}
            <Line
              dataKey="primary"
              stroke="#4F9EEC"
              strokeWidth={2.25}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              tickFormatter={xTickFormatter}
              tick={{ fontSize: 11, fill: '#828B9E' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              width={56}
              tickFormatter={tickFormatterFor(unit)}
              tick={{ fontSize: 11, fill: '#828B9E' }}
              domain={[0, yMax != null ? yMax * 1.05 : 'auto']}
              allowDataOverflow
            />
          </ComposedChart>
        </ResponsiveContainer>
        {isClipped && (
          <div className="text-light text-small m-t-1 anomalies-chart-clip-note">
            Peak values clipped for readability — switch to Full to see them.
          </div>
        )}
      </div>
    </div>
  )
}

const chartTitleFor = (
  m: TAnomaliesPrimaryMetric,
  family: TAnomaliesAlertFamily
): string => {
  // Rejection alerts are volume-shaped now — show "Reported rejection volume"
  // not "Rejection rate trend".
  if (family === 'rejection') return 'Reported rejection volume (7d rolling)'
  if (m === 'cr') return 'Conversion rate trend'
  if (m === 'epc') return 'EPC trend'
  if (m === 'spc') return 'Sale per click trend'
  if (m === 'clicks') return 'Click volume'
  if (m === 'conversions') return 'Conversion volume'
  if (m === 'rejection_rate') return 'Rejection rate trend'
  return `${METRIC_LABELS[m]} trend`
}

const Legend = (props: { color: string; label: string; dashed?: boolean }) => (
  <span className="anomalies-chart-legend-item">
    <span
      className="anomalies-chart-legend-line"
      style={{
        borderTopColor: props.color,
        borderTopStyle: props.dashed ? 'dashed' : 'solid',
      }}
    />
    {props.label}
  </span>
)

const PrimaryTooltip = (props: any) => {
  const {
    active,
    payload,
    label,
    metric,
    primaryLabel,
    referenceLabel,
    rollingLabel,
    forceInteger,
  } = props
  if (!active || !payload || !payload.length) return null

  const row = payload[0]?.payload
  if (!row) return null

  const rows: { color: string; label: string; value: number | null }[] = []
  if (row.primary != null) rows.push({ color: '#4F9EEC', label: primaryLabel, value: row.primary })
  if (row.rolling != null && rollingLabel)
    rows.push({ color: '#5DC97E', label: rollingLabel, value: row.rolling })
  if (row.reference != null && referenceLabel)
    rows.push({ color: '#828B9E', label: referenceLabel, value: row.reference })

  const fmt = (value: number | null): string =>
    forceInteger
      ? value == null
        ? '—'
        : formatInteger(Math.round(value))
      : formatMetricValue(value, metric)

  return (
    <div className="anomalies-chart-tooltip">
      <div className="anomalies-chart-tooltip-date">
        {label ? format(parseISO(label), 'MMM d, yyyy') : ''}
      </div>
      {rows.map((r) => (
        <div key={r.label} className="anomalies-chart-tooltip-row">
          <span
            className="anomalies-chart-tooltip-dot"
            style={{ background: r.color }}
          />
          <span className="anomalies-chart-tooltip-label">{r.label}</span>
          <span className="anomalies-chart-tooltip-value">{fmt(r.value)}</span>
        </div>
      ))}
    </div>
  )
}
